import React from 'react';
import { Link } from 'react-router-dom';
import { Download, ClipboardCheck } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { formatRial } from '@/lib/formatMoney';
import type { BsiIbLastThirtyItem, BsiStatementRegistrationSummary } from '@/lib/banks/bsi/api';
import {
  employeeProfilePath,
  projectBeneficiaryPath,
} from '@/pages/project-workspace/projectWorkspaceHelpers';

type Props = {
  title: string;
  items: BsiIbLastThirtyItem[];
  registrations?: BsiStatementRegistrationSummary[];
  onRegister?: (index: number) => void;
  onExport?: () => void;
  creditLabel?: string;
  debitLabel?: string;
};

function projectHref(reg?: BsiStatementRegistrationSummary): string | null {
  if (!reg?.project_id) return null;
  return `/projects/${reg.project_id}/overview`;
}

function expenseTypeHref(reg?: BsiStatementRegistrationSummary): string | null {
  if (!reg?.expense_type_id) return null;
  return `/expense-types?type=${reg.expense_type_id}`;
}

function counterpartyHref(reg?: BsiStatementRegistrationSummary): string | null {
  if (!reg?.counterparty_type) return null;
  if (reg.counterparty_type === 'other') return null;
  if (reg.counterparty_type === 'employee' && reg.counterparty_id) {
    return employeeProfilePath(reg.counterparty_id);
  }
  if (
    (reg.counterparty_type === 'client' || reg.counterparty_type === 'company') &&
    reg.counterparty_id &&
    reg.project_id
  ) {
    return projectBeneficiaryPath(reg.project_id, `${reg.counterparty_type}-${reg.counterparty_id}`);
  }
  return null;
}

function LinkedCell({
  href,
  title,
  children,
}: {
  href: string | null;
  title?: string;
  children: React.ReactNode;
}) {
  if (!href || children === '—' || children == null) {
    return (
      <td className="border px-2 py-1.5 max-w-[140px] truncate" title={title}>
        {children ?? '—'}
      </td>
    );
  }
  return (
    <td className="border px-2 py-1.5 max-w-[140px] truncate" title={title}>
      <Link
        to={href}
        className="text-sky-700 underline-offset-2 hover:text-sky-900 hover:underline"
        onClick={(e) => e.stopPropagation()}
      >
        {children}
      </Link>
    </td>
  );
}

const BsiTransactionsTable: React.FC<Props> = ({
  title,
  items,
  registrations = [],
  onRegister,
  onExport,
  creditLabel = 'جمع واریز',
  debitLabel = 'جمع برداشت',
}) => {
  const lastBalance = items[0]?.balance ?? null;
  const totals = React.useMemo(
    () =>
      items.reduce(
        (acc, r) => {
          acc.credit += r.credit || 0;
          acc.debit += r.debit || 0;
          return acc;
        },
        { credit: 0, debit: 0 },
      ),
    [items],
  );

  const registeredCount = registrations.filter((r) => r?.registered).length;

  return (
    <Card>
      <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-3 space-y-0">
        <div className="space-y-1">
          <CardTitle className="text-base">{title}</CardTitle>
          {registrations.length > 0 ? (
            <p className="text-xs text-slate-500">
              {registeredCount} از {items.length} ردیف ثبت شده
            </p>
          ) : null}
        </div>
        {onExport ? (
          <Button type="button" variant="outline" size="sm" onClick={onExport}>
            <Download className="ml-1 h-4 w-4" />
            خروجی اکسل
          </Button>
        ) : null}
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <div className="rounded-md border bg-slate-50 px-3 py-2 text-sm">
            <div className="text-xs text-slate-500">مانده آخرین ردیف</div>
            <div className="font-mono font-semibold">{lastBalance != null ? formatRial(lastBalance) : '—'}</div>
          </div>
          <div className="rounded-md border bg-emerald-50 px-3 py-2 text-sm">
            <div className="text-xs text-emerald-700">{creditLabel}</div>
            <div className="font-mono font-semibold text-emerald-800">{formatRial(totals.credit)}</div>
          </div>
          <div className="rounded-md border bg-rose-50 px-3 py-2 text-sm">
            <div className="text-xs text-rose-700">{debitLabel}</div>
            <div className="font-mono font-semibold text-rose-800">{formatRial(totals.debit)}</div>
          </div>
        </div>
        <div className="overflow-x-auto rounded border">
          <table className="w-full min-w-[1200px] text-sm">
            <thead>
              <tr className="bg-slate-100 text-right">
                <th className="border px-2 py-2">#</th>
                <th className="border px-2 py-2">تاریخ</th>
                <th className="border px-2 py-2">زمان</th>
                <th className="border px-2 py-2">شعبه</th>
                <th className="border px-2 py-2">نوع</th>
                <th className="border px-2 py-2">شماره سند</th>
                <th className="border px-2 py-2">شرح</th>
                <th className="border px-2 py-2">واریز</th>
                <th className="border px-2 py-2">برداشت</th>
                <th className="border px-2 py-2">مانده</th>
                <th className="border px-2 py-2">ثبت‌شده</th>
                <th className="border px-2 py-2">پروژه</th>
                <th className="border px-2 py-2">نوع هزینه</th>
                <th className="border px-2 py-2">طرف حساب</th>
                {onRegister ? <th className="border px-2 py-2">عملیات</th> : null}
              </tr>
            </thead>
            <tbody>
              {items.map((r, index) => {
                const reg = registrations[index];
                const isRegistered = Boolean(reg?.registered);

                return (
                  <tr key={`${r.row}-${r.date}-${r.time}-${r.doc_no}-${index}`}>
                    <td className="border px-2 py-1.5">{r.row}</td>
                    <td className="border px-2 py-1.5 whitespace-nowrap">{r.date || '—'}</td>
                    <td className="border px-2 py-1.5 whitespace-nowrap">{r.time || '—'}</td>
                    <td className="border px-2 py-1.5">{r.branch || '—'}</td>
                    <td className="border px-2 py-1.5">{r.type || '—'}</td>
                    <td className="border px-2 py-1.5 font-mono text-xs">{r.doc_no || '—'}</td>
                    <td className="border px-2 py-1.5 max-w-[220px] truncate" title={r.description}>
                      {r.description || '—'}
                    </td>
                    <td className="border px-2 py-1.5 font-mono text-emerald-700">
                      {r.credit ? formatRial(r.credit) : '—'}
                    </td>
                    <td className="border px-2 py-1.5 font-mono text-rose-700">
                      {r.debit ? formatRial(r.debit) : '—'}
                    </td>
                    <td className="border px-2 py-1.5 font-mono">{formatRial(r.balance)}</td>
                    <td className="border px-2 py-1.5">
                      {isRegistered ? (
                        <Badge variant="default" className="bg-emerald-600 hover:bg-emerald-600">
                          بله
                        </Badge>
                      ) : (
                        <Badge variant="secondary">خیر</Badge>
                      )}
                    </td>
                    <LinkedCell href={projectHref(reg)} title={reg?.project_label || undefined}>
                      {reg?.project_label || '—'}
                    </LinkedCell>
                    <LinkedCell href={expenseTypeHref(reg)} title={reg?.expense_type_title || undefined}>
                      {reg?.expense_type_title || '—'}
                    </LinkedCell>
                    <LinkedCell href={counterpartyHref(reg)} title={reg?.counterparty_label || undefined}>
                      {reg?.counterparty_label || '—'}
                    </LinkedCell>
                    {onRegister ? (
                      <td className="border px-2 py-1.5">
                        <Button
                          type="button"
                          variant={isRegistered ? 'outline' : 'secondary'}
                          size="sm"
                          className="h-8 whitespace-nowrap"
                          onClick={() => onRegister(index)}
                        >
                          <ClipboardCheck className="ml-1 h-3.5 w-3.5" />
                          {isRegistered ? 'ویرایش' : 'ثبت'}
                        </Button>
                      </td>
                    ) : null}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
};

export default BsiTransactionsTable;
